import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SelectFieldOption,
  SelectFieldPinnedOptions,
  SelectFieldPinnedOptionsSection,
} from "../../beta/components/SelectField/types";

const DEFAULT_CACHE_SIZE = 15;
const STATIC_CACHE_KEY = "__static__";

/**
 * Normalized pinned section structure used internally.
 * @property label - Section label (e.g., "Recent", "Favorites")
 * @property options - The pinned options for this section
 * @property loading - Whether this section is still loading its options
 */
export type NormalizedPinnedSection = {
  label: string;
  options: SelectFieldOption[];
  loading?: boolean;
};

type UsePinnedOptionsResult = {
  pinnedSections: NormalizedPinnedSection[];
};

type SectionConfig = {
  label: string;
  isDynamic: boolean;
  searchReactive: boolean;
  cacheSize: number;
  staticOptions: SelectFieldOption[] | undefined;
  loader:
    | ((sv: string) => SelectFieldOption[] | Promise<SelectFieldOption[]>)
    | undefined;
};

/**
 * Insert into a section's cache with max-size eviction.
 * Evicts the oldest entry (first key in Map) when the limit is exceeded.
 */
function cacheSet(
  sectionCache: Map<string, SelectFieldOption[]>,
  key: string,
  value: SelectFieldOption[],
  maxSize: number,
): void {
  // If this key already exists, delete it first so it moves to the end (most recent)
  if (sectionCache.has(key)) {
    sectionCache.delete(key);
  }
  sectionCache.set(key, value);
  // Evict oldest entries if over max size
  while (sectionCache.size > maxSize) {
    const oldest = sectionCache.keys().next().value;
    if (oldest !== undefined) {
      sectionCache.delete(oldest);
    }
  }
}

/**
 * Hook that normalizes and loads pinned options from various input formats.
 *
 * Handles:
 * - Labeled pinned options object
 * - Array of labeled pinned options objects
 *
 * Dynamic sections appear immediately with `loading: true` and empty options,
 * then transition to their resolved options once loaded. Results are cached
 * per section per search value so revisiting a previous search term is instant.
 *
 * Per-section configuration:
 * - `searchReactive` (default true): when false, loader is called once and the
 *   result is reused for all search values.
 * - `cacheSize` (default 15): max number of search results cached per section.
 *
 * @param pinned - The pinned options configuration from props
 * @param searchValue - Current search input value
 * @returns Normalized pinned sections with per-section loading state
 */
export function usePinnedOptions(
  pinned: SelectFieldPinnedOptions | undefined,
  searchValue: string,
): UsePinnedOptionsResult {
  const [dynamicResults, setDynamicResults] = useState<
    Record<number, SelectFieldOption[]>
  >({});
  const [loadingSet, setLoadingSet] = useState<Set<number>>(new Set());

  // Track the latest search value to avoid race conditions
  const latestSearchValueRef = useRef(searchValue);
  latestSearchValueRef.current = searchValue;

  // Per-section cache: sectionIndex -> Map<searchValue, options>
  const cacheRef = useRef<Map<number, Map<string, SelectFieldOption[]>>>(
    new Map(),
  );

  // Track non-reactive sections that have already been triggered to prevent
  // duplicate loader calls when search value changes during initial load
  const nonReactiveTriggeredRef = useRef<Set<number>>(new Set());

  // Normalize into an array of labeled sections
  const sections = useMemo((): SelectFieldPinnedOptionsSection[] => {
    if (!pinned) return [];
    return Array.isArray(pinned) ? pinned : [pinned];
  }, [pinned]);

  // Classify each section as static or dynamic, with per-section config
  const sectionConfig = useMemo((): SectionConfig[] => {
    return sections.map((section) => {
      const isDynamic = typeof section.options === "function";
      return {
        label: section.label,
        isDynamic,
        searchReactive: section.searchReactive ?? true,
        cacheSize: section.cacheSize ?? DEFAULT_CACHE_SIZE,
        staticOptions: isDynamic
          ? undefined
          : (section.options as SelectFieldOption[]),
        loader: isDynamic
          ? (section.options as (
              sv: string,
            ) => SelectFieldOption[] | Promise<SelectFieldOption[]>)
          : undefined,
      };
    });
  }, [sections]);

  // Clear cache and non-reactive tracking when section config changes.
  // Runs before the loader effect (declared first) so stale entries are
  // purged before any new loads read from the cache.
  useEffect(() => {
    cacheRef.current = new Map();
    nonReactiveTriggeredRef.current = new Set();
  }, [sectionConfig]);

  // Get or create a section's cache map
  const getSectionCache = useCallback(
    (index: number): Map<string, SelectFieldOption[]> => {
      let sectionCache = cacheRef.current.get(index);
      if (!sectionCache) {
        sectionCache = new Map();
        cacheRef.current.set(index, sectionCache);
      }
      return sectionCache;
    },
    [],
  );

  // Load a single dynamic section, updating state and cache on completion
  const loadSection = useCallback(
    (config: SectionConfig, index: number, loaderArg: string) => {
      const cacheKey = config.searchReactive ? loaderArg : STATIC_CACHE_KEY;

      const handleResult = (options: SelectFieldOption[]) => {
        // Non-reactive sections: always apply (no race condition, loaded once).
        // Reactive sections: only apply if this is still the latest search.
        if (
          !config.searchReactive ||
          latestSearchValueRef.current === loaderArg
        ) {
          const sectionCache = getSectionCache(index);
          cacheSet(sectionCache, cacheKey, options, config.cacheSize);
          setDynamicResults((prev) => ({ ...prev, [index]: options }));
          setLoadingSet((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
        }
      };

      const handleError = (error: unknown) => {
        console.error("Failed to load pinned options:", error);
        if (
          !config.searchReactive ||
          latestSearchValueRef.current === loaderArg
        ) {
          setDynamicResults((prev) => ({ ...prev, [index]: [] }));
          setLoadingSet((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
        }
      };

      try {
        Promise.resolve(config.loader!(loaderArg))
          .then(handleResult)
          .catch(handleError);
      } catch (error) {
        handleError(error);
      }
    },
    [getSectionCache],
  );

  // Load dynamic options when searchValue or config changes
  useEffect(() => {
    const dynamicEntries = sectionConfig
      .map((config, index) => ({ config, index }))
      .filter(({ config }) => config.isDynamic);

    if (dynamicEntries.length === 0) return;

    const toLoad: { config: SectionConfig; index: number }[] = [];
    const preResolved: Record<number, SelectFieldOption[]> = {};

    dynamicEntries.forEach(({ config, index }) => {
      const sectionCache = getSectionCache(index);

      if (config.searchReactive) {
        // Reactive section: check cache for this specific search value
        const cached = sectionCache.get(searchValue);
        if (cached) {
          preResolved[index] = cached;
        } else {
          toLoad.push({ config, index });
        }
      } else {
        // Non-reactive section: use static cache entry, load at most once
        const cached = sectionCache.get(STATIC_CACHE_KEY);
        if (cached) {
          preResolved[index] = cached;
        } else if (!nonReactiveTriggeredRef.current.has(index)) {
          nonReactiveTriggeredRef.current.add(index);
          toLoad.push({ config, index });
        }
        // else: already triggered, still loading - shows as loading via fallthrough
      }
    });

    // If everything is cached, just set results immediately
    if (toLoad.length === 0) {
      setDynamicResults(preResolved);
      setLoadingSet(new Set());
      return;
    }

    // Set pre-resolved results and mark only uncached sections as loading
    const loadingIndices = toLoad.map(({ index }) => index);
    setDynamicResults(preResolved);
    setLoadingSet(new Set(loadingIndices));

    // Load each uncached dynamic section
    toLoad.forEach(({ config, index }) => {
      // Non-reactive sections always pass "" to the loader
      const loaderArg = config.searchReactive ? searchValue : "";
      loadSection(config, index, loaderArg);
    });
  }, [sectionConfig, searchValue, loadSection, getSectionCache]);

  // Build the final sections array with per-section loading state
  const pinnedSections = useMemo((): NormalizedPinnedSection[] => {
    return sectionConfig.map((config, index) => {
      if (!config.isDynamic) {
        return { label: config.label, options: config.staticOptions! };
      }
      // Dynamic section: show resolved results if available and not loading
      if (!loadingSet.has(index) && index in dynamicResults) {
        return {
          label: config.label,
          options: dynamicResults[index],
        };
      }
      // Otherwise it's still loading (or hasn't started yet)
      return { label: config.label, options: [], loading: true };
    });
  }, [sectionConfig, dynamicResults, loadingSet]);

  return { pinnedSections };
}
